import { type ContentItem } from '@/api/v4/content-items.api';
import { Button } from '@/ui/button/button';
import { Inline, Stack } from '@/ui/line/line';
import { Widget } from '@/ui/widget/widget';
import { styled } from 'goober';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateProject } from '../projects/use-create-project.mutation';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { TagWithEllipsis } from '@/components/tag/tag-with-ellipsis';
import { EllipsisTextTooltip } from '@/ui/table/infinite-table/ellipsis-text-tooltip';

type FeedCardProps = {
  feed: ContentItem;
  showCreateProjectButton: boolean;
  showGoToProjectButton: boolean;
  handleGoToProject: () => void;
  handleLearnMore: () => void;
  isGoToProjectLoading: boolean;
  isLoading: boolean;
};

export const FeedCard = ({
  feed,
  showCreateProjectButton,
  showGoToProjectButton,
  handleGoToProject,
  isGoToProjectLoading,
  isLoading,
  handleLearnMore,
}: FeedCardProps) => {
  const { t } = useTranslation('feed', { keyPrefix: 'card' });
  const navigateTo = useNavigateTo();

  const { createProject } = useCreateProject();

  const createNewProjectFromFeed = async () => {
    const project = await createProject({
      name: feed.title,
      problemStatement: feed.longDescription ?? feed.description,
      tagIds: feed.tags.map(t => t.id),
    });
    navigateTo.project({ projectId: project.id });
  };

  return (
    <FeedWidget
      editable={false}
      showEditButton={false}
      padding="16px"
      dataTestId="feed-card"
    >
      <CardContainer gap="21px">
        <Stack gap="11px" fullWidth>
          <ImageContainer>
            {feed.image?.url ? (
              <Image src={feed.image.url} alt="feed" />
            ) : (
              <ImagePlaceholder />
            )}
          </ImageContainer>
          <Stack>
            <EllipsisTextTooltip
              text={feed.title}
              Component={<Title data-testid="feed-card-title" />}
            />
            <EllipsisTextTooltip
              text={feed.description}
              Component={<Description data-testid="feed-card-description" />}
            />
          </Stack>
          <TagsContainer
            alignItems="self-start"
            wrap="wrap"
            gap="10px"
            fullHeight
            fullWidth
          >
            {feed.tags.map(tag => (
              <TagWithEllipsis
                key={tag.id}
                maxWidth="100%"
                text={tag.name}
                variant="blue"
              />
            ))}
          </TagsContainer>
        </Stack>
        <Inline width="max-content">
          <Button
            disabled={isLoading}
            onClick={handleLearnMore}
          >{t`learnMore`}</Button>
          {showGoToProjectButton && (
            <TextButton
              disabled={isGoToProjectLoading}
              variant="text"
              onClick={handleGoToProject}
            >{t`goToProject`}</TextButton>
          )}
          {showCreateProjectButton && (
            <TextButton
              variant="text"
              onClick={createNewProjectFromFeed}
            >{t`createProject`}</TextButton>
          )}
        </Inline>
      </CardContainer>
    </FeedWidget>
  );
};

const FeedWidget = styled(Widget)`
  height: 100%;
`;

const CardContainer = styled(Stack)`
  grid-template-rows: 1fr auto;
  min-height: 100%;
  width: 100%;
`;

const imageProperties = `
  width: 100%;
  height: 100%;
  border-radius: 8px;
`;

const Image = styled('img')`
  ${imageProperties};

  object-fit: cover;
`;

const ImagePlaceholder = styled('div')`
  ${imageProperties};

  background-color: gray;
`;

const ImageContainer = styled('div')`
  height: 120px;
  align-self: stretch;
  width: 100%;
`;

const Title = styled('h2', forwardRef)`
  ${({ theme }) => theme.typography.navigationPanel.selected}
  ${({ theme }) => theme.mixins.lineClamp(2)}
`;

const Description = styled('p', forwardRef)`
  ${({ theme }) => theme.typography.actionsPanel.acrossPanel.smallText}
  ${({ theme }) => theme.mixins.lineClamp(4)}
`;

const TextButton = styled(Button)`
  color: ${({ theme }) => theme.colors.blue.primaryA};
  &:hover {
    color: ${({ theme }) => theme.colors.blue.primaryA};
  }
`;

const TagsContainer = styled(Flexbox)`
  min-width: 0;
`;
